
import Vue from "vue"
import axios, { AxiosError, AxiosResponse } from "axios"
import { EventBus } from "@/plugins/eventBus"
import { state } from "@/plugins/state"
import CompanyAutocomplete from "@/components/company/CompanyAutocomplete.vue"
import AddressSearchInput from "@/components/AddressSearchInput.vue"

interface PositionBody {
  client_id: string | null
  name: string
  hour_rate: number | null
  extra_primes: string | null
  extra_advantage: string | null
  address: string
  city: string
  zipcode: string
  address_position: { latitude: number; longitude: number } | null
  phone: string | null
  phone_unformatted: string | null
  contact_id: number | null
  contact_firstname: string | null
  contact_lastname: string | null
  description: string | null
  applicant_profile: string | null
  complementary_information: string | null
  valid_worker_by_sms: boolean
  default_confirmation_message_comment: string | null
  cycle: { schedule: string | null; duration: number | null } | null
  competences?: string[]
}

export default Vue.extend({
  name: "PositionForm",
  components: {
    CompanyAutocomplete,
    AddressSearchInput
  },
  data: () => ({
    activePanels: [0,1,2,3,4,5,6],
    isCreatingPosition: false,
    clientId: null as string | null,
    name: "",
    address: "",
    zipcode: "",
    city: "",
    latitude: null as number | null,
    longitude: null as number | null,
    phone: "",
    contactId: null as number | null,
    contactFirstname: "",
    contactLastname: "",
    applicantProfile: "",
    description: "",
    complementaryInformation: "",
    hourRate: null as number | null,
    extraPrimes: "",
    extraAdvantage: "",
    validWorkerBySms: false,
    defaultConfirmationMessageComment: "",
    addressSearch: "",
    selectedAddressCoordinates: null as { latitude: number; longitude: number } | null,
    cycleSchedule: "",
    cycleDuration: null as number | null,
    competences: [""] as string[],
    competenceOptions: { 0: [] } as Record<number, Array<{ id: number; position_id: number; label_competence: string }>>,
    competenceSearchInputs: { 0: "" } as Record<number, string>,
    competenceSearchTimeouts: {} as Record<number, ReturnType<typeof setTimeout> | null>,
    competenceLoading: { 0: false } as Record<number, boolean>,
  }),
  beforeDestroy() {
    // Clear all timeouts on component destruction
    Object.values(this.competenceSearchTimeouts).forEach(timeout => {
      if (timeout) {
        clearTimeout(timeout)
      }
    })
  },
  computed: {
    requiredRule() {
      return (v: string) => !!v || this.$vuetify.lang.t('$vuetify.required_field')
    },
    stringLengthRule() {
      return (v: string) => ((v && v.length > 1 && v.length < 200) || !v) || this.$vuetify.lang.t('$vuetify.invalid_length')
    },
    phoneRule() {
      return (v: string) => {
        if (!v) return true
        // Allow only numeric characters and '+'
        const validPattern = /^[0-9+]+$/
        return validPattern.test(v) || this.$vuetify.lang.t('$vuetify.invalid_phone_number')
      }
    },
  },
  methods: {
    onCompanySelected(id: string | undefined) {
      this.clientId = id || null
    },
    /**
     * Handle address selection from AddressSearchInput
     */
    onAddressSelected(coordinates: [number, number]) {
      if (coordinates && coordinates.length === 2) {
        const [longitude, latitude] = coordinates
        this.selectedAddressCoordinates = { latitude, longitude }
        this.latitude = latitude
        this.longitude = longitude
      }
    },
    /**
     * Handle address details selection from AddressSearchInput
     */
    onAddressDetailsSelected(address: any) {
      if (address && address.properties) {
        const props = address.properties

        // Extract address components
        this.address = props.name || ''
        this.zipcode = props.postcode || ''
        this.city = props.city || ''

        // Extract coordinates
        if (address.geometry && address.geometry.coordinates) {
          const [longitude, latitude] = address.geometry.coordinates
          this.latitude = latitude
          this.longitude = longitude
          this.selectedAddressCoordinates = { latitude, longitude }
        }
      }
    },
    /**
     * Handle input-cleared event from AddressSearchInput
     */
    handleAddressInputCleared() {
      // Clear the address search when input is cleared
      this.addressSearch = ''
      this.selectedAddressCoordinates = null
      this.latitude = null
      this.longitude = null
      this.address = ''
      this.zipcode = ''
      this.city = ''
    },
    addTask() {
      this.competences.push("")
      const newIndex = this.competences.length - 1
      // Use $set to ensure Vue reactivity for newly added properties
      this.$set(this.competenceOptions, newIndex, [])
      this.$set(this.competenceSearchInputs, newIndex, "")
      this.$set(this.competenceLoading, newIndex, false)
    },
    removeTask(index: number) {
      if (this.competences.length > 1) {
        this.competences.splice(index, 1)
        // Clean up the corresponding autocomplete data
        delete this.competenceOptions[index]
        delete this.competenceSearchInputs[index]
        delete this.competenceLoading[index]
        if (this.competenceSearchTimeouts[index]) {
          clearTimeout(this.competenceSearchTimeouts[index]!)
          delete this.competenceSearchTimeouts[index]
        }
        // Reindex the arrays after deletion
        this.reindexCompetenceData(index)
      }
    },
    reindexCompetenceData(removedIndex: number) {
      // Reindex competenceOptions
      const newOptions: Record<number, Array<{ id: number; position_id: number; label_competence: string }>> = {}
      Object.keys(this.competenceOptions).forEach(key => {
        const oldIndex = Number.parseInt(key)
        if (oldIndex < removedIndex) {
          newOptions[oldIndex] = this.competenceOptions[oldIndex]
        }
        if (oldIndex > removedIndex) {
          newOptions[oldIndex - 1] = this.competenceOptions[oldIndex]
        }
      })
      this.competenceOptions = newOptions

      // Reindex competenceSearchInputs
      const newInputs: Record<number, string> = {}
      Object.keys(this.competenceSearchInputs).forEach(key => {
        const oldIndex = Number.parseInt(key)
        if (oldIndex < removedIndex) {
          newInputs[oldIndex] = this.competenceSearchInputs[oldIndex]
        }
        if (oldIndex > removedIndex) {
          newInputs[oldIndex - 1] = this.competenceSearchInputs[oldIndex]
        }
      })
      this.competenceSearchInputs = newInputs

      // Reindex competenceSearchTimeouts
      const newTimeouts: Record<number, ReturnType<typeof setTimeout> | null> = {}
      Object.keys(this.competenceSearchTimeouts).forEach(key => {
        const oldIndex = Number.parseInt(key)
        if (oldIndex < removedIndex) {
          newTimeouts[oldIndex] = this.competenceSearchTimeouts[oldIndex]
        }
        if (oldIndex > removedIndex) {
          newTimeouts[oldIndex - 1] = this.competenceSearchTimeouts[oldIndex]
        }
      })
      this.competenceSearchTimeouts = newTimeouts

      // Reindex competenceLoading
      const newLoading: Record<number, boolean> = {}
      Object.keys(this.competenceLoading).forEach(key => {
        const oldIndex = Number.parseInt(key)
        if (oldIndex < removedIndex) {
          newLoading[oldIndex] = this.competenceLoading[oldIndex]
        }
        if (oldIndex > removedIndex) {
          newLoading[oldIndex - 1] = this.competenceLoading[oldIndex]
        }
      })
      this.competenceLoading = newLoading
    },
    onCompetenceSearch(index: number, searchValue: string | null) {
      if (searchValue === null) {
        this.$set(this.competenceLoading, index, false)
        return
      }

      // Ensure the properties exist for this index (for newly added inputs)
      if (!(index in this.competenceOptions)) {
        this.$set(this.competenceOptions, index, [])
      }
      if (!(index in this.competenceSearchInputs)) {
        this.$set(this.competenceSearchInputs, index, searchValue)
      }
      if (!(index in this.competenceLoading)) {
        this.$set(this.competenceLoading, index, false)
      }

      // Clear previous timeout for this index
      if (this.competenceSearchTimeouts[index]) {
        clearTimeout(this.competenceSearchTimeouts[index]!)
      }

      // Start loading animation when debounce timer starts
      this.$set(this.competenceLoading, index, true)

      // Debounce search - wait 1 second after user stops typing
      // Search can be executed with 1 character minimum
      this.competenceSearchTimeouts[index] = setTimeout(() => {
        this.searchCompetences(index, searchValue)
      }, 1000)
    },
    async searchCompetences(index: number, searchValue: string) {
      if (!searchValue || searchValue.trim().length === 0) {
        this.$set(this.competenceOptions, index, [])
        this.$set(this.competenceLoading, index, false)
        return
      }

      try {
        const params: { data: string; search: string; space_id?: string } = {
          data: 'position_task',
          search: searchValue.trim()
        }
        
        if (state.agency) {
          params.space_id = state.agency
        }

        const response: AxiosResponse<Array<{ id: number; position_id: number; label_competence: string }>> = await axios.get(
          `/v1/autocomplete`,
          { params }
        )
        this.$set(this.competenceOptions, index, response.data)
      } catch (error) {
        EventBus.$emit('snackbar', { 
          message: this.$vuetify.lang.t('$vuetify.error_occurred_while_searching_competences'), 
          color: 'error',
          axiosError: (error as AxiosError).response 
        })
        this.$set(this.competenceOptions, index, [])
      } finally {
        // Stop loading animation when data is fetched (success or error)
        this.$set(this.competenceLoading, index, false)
      }
    },
    onCompetenceChange(index: number, value: string | { label_competence: string } | null) {
      // Ensure we always store a string value
      // If value is an object (selected from dropdown), extract label_competence
      // If value is a string (typed custom value), use it directly
      if (value === null || value === undefined) {
        this.competences[index] = ""
      } else if (typeof value === 'string') {
        this.competences[index] = value
      } else if (typeof value === 'object' && 'label_competence' in value) {
        this.competences[index] = value.label_competence
      } else {
        // Fallback: convert to string
        this.competences[index] = String(value)
      }
    },
    savePosition() {
      const formIsValid = (this.$refs.form as Vue & { validate: () => boolean }).validate()
      if (!formIsValid) {
        return
      }

      this.isCreatingPosition = true
      const body: PositionBody = {
        client_id: this.clientId,
        name: this.name,
        hour_rate: this.hourRate ?? null,
        extra_primes: this.extraPrimes || null,
        extra_advantage: this.extraAdvantage || null,
        address: this.address,
        city: this.city,
        zipcode: this.zipcode,
        address_position: this.latitude != null && this.longitude != null ? { latitude: this.latitude, longitude: this.longitude } : null,
        phone: this.phone || null,
        phone_unformatted: this.phone ? this.phone.replaceAll(/[^\d]/g, '') : null,
        contact_id: this.contactId ?? null,
        contact_firstname: this.contactFirstname || null,
        contact_lastname: this.contactLastname || null,
        description: this.description || null,
        applicant_profile: this.applicantProfile || null,
        complementary_information: this.complementaryInformation || null,
        valid_worker_by_sms: this.validWorkerBySms,
        default_confirmation_message_comment: this.defaultConfirmationMessageComment || null,
        cycle: {
          schedule: this.cycleSchedule || null,
          duration: this.cycleDuration ?? null
        }
      }

      // Add competences only if the array is not empty (after filtering out empty strings)
      const nonEmptyCompetences = this.competences.filter(task => task.trim() !== "")
      if (nonEmptyCompetences.length > 0) {
        body.competences = nonEmptyCompetences
      }

      axios
        .post(`/v1/positions`, body)
        .then((_response: AxiosResponse) => {
          EventBus.$emit('snackbar', { message: this.$vuetify.lang.t('$vuetify.position_created_successfully') })
        })
        .catch((error: AxiosError) => {
          EventBus.$emit('snackbar', { message: this.$vuetify.lang.t('$vuetify.error_occurred_while_creating_position'), color: 'error', axiosError: error.response })
        })
        .finally(() => {
          this.isCreatingPosition = false
        })
    }
  }
})
